import React from 'react';
import { ViewDSL, Section, SectionRegistry, SectionManifest } from './types';

export class DezzyCompiler {
  private registry: SectionRegistry;

  constructor(registry: SectionRegistry = {}) {
    this.registry = registry;
  }

  registerSection(id: string, component: React.ComponentType<any>) {
    this.registry[id] = component;
  }

  compileView(dsl: ViewDSL): React.ComponentType<any> {
    return (props: any) => {
      const { layout = { type: 'stack', gap: 'space.md' }, sections } = dsl;
      
      const layoutStyles: React.CSSProperties = {
        display: layout.type === 'grid' ? 'grid' : 'flex',
        ...(layout.type === 'grid' && {
          gridTemplateColumns: `repeat(${layout.cols || 12}, 1fr)`,
        }),
        ...(layout.type === 'stack' && {
          flexDirection: 'column',
        }),
        gap: `var(--${layout.gap?.replace(/\./g, '-')})`,
      };

      return React.createElement(
        'main',
        { 
          className: 'dezzy-view',
          style: layoutStyles,
          'data-view-id': dsl.meta.id,
          role: 'main'
        },
        ...sections.map((section, index) => this.renderSection(section, index, props))
      );
    };
  }

  private renderSection(section: Section, index: number, viewProps: any) {
    const Component = this.registry[section.use];
    if (!Component) {
      console.warn(`Section not found: ${section.use}`);
      return React.createElement('div', { 
        key: index,
        className: 'dezzy-section-missing' 
      }, `Missing section: ${section.use}`);
    }

    const gridStyles: React.CSSProperties = section.grid ? {
      gridColumnStart: section.grid.colStart,
      gridColumnEnd: `span ${section.grid.colSpan}`,
    } : {};

    const resolvedProps = this.resolveBindings(section.bindings || {}, viewProps);

    return React.createElement(
      'div',
      {
        key: index,
        className: `dezzy-section dezzy-section--${section.use.replace('section.', '')}`,
        style: gridStyles,
        'data-section-id': section.use,
        'data-variant': section.variant || 'default',
      },
      React.createElement(Component, {
        ...resolvedProps,
        variant: section.variant || 'default',
      })
    );
  }

  resolveBinding(binding: string, ctx: any, actions: any): any {
    if (binding.startsWith('ctx.')) {
      const path = binding.substring(4);
      return this.getNestedValue(ctx, path);
    } else if (binding.startsWith('actions.')) {
      const path = binding.substring(8);
      return this.getNestedValue(actions, path);
    } else if (binding.startsWith('static:')) {
      return binding.substring(7); // Remove 'static:' prefix
    } else {
      return binding; // Static value
    }
  }

  private resolveBindings(bindings: Record<string, string>, viewProps: any): any {
    const resolved: any = {};
    
    for (const [key, binding] of Object.entries(bindings)) {
      if (binding.startsWith('ctx.')) {
        // Context binding: ctx.loan -> viewProps.ctx.loan
        const path = binding.substring(4);
        resolved[key] = this.getNestedValue(viewProps.ctx || {}, path);
      } else if (binding.startsWith('actions.')) {
        // Action binding: actions.openPayment -> viewProps.actions.openPayment
        const actionName = binding.substring(8);
        resolved[key] = viewProps.actions?.[actionName];
      } else if (binding.startsWith('static:')) {
        // Static value with prefix: static:Features -> Features
        resolved[key] = binding.substring(7);
      } else {
        // Direct value
        resolved[key] = binding;
      }
    }
    
    return resolved;
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  // Agent-specific compilation methods
  compileSection(manifest: SectionManifest): React.ComponentType<any> {
    return (props: any) => {
      const { variant = 'default', ...otherProps } = props;
      
      // Find the variant in the manifest
      const variantDef = manifest.variants?.find(v => v.name === variant);
      
      // Merge default props with variant props
      const mergedProps = {
        ...manifest.defaultProps,
        ...variantDef?.props,
        ...otherProps
      };

      // Apply variant tokens as CSS custom properties
      const tokenStyles: React.CSSProperties = {};
      if (variantDef?.tokens) {
        Object.entries(variantDef?.tokens).forEach(([key, value]) => {
          // Convert token references to CSS custom properties
          const cssVar = value.startsWith('$') 
            ? `var(--${value.substring(1).replace(/\./g, '-')})` 
            : value;
          (tokenStyles as any)[`--${key}`] = cssVar;
        });
      }

      // Create component class names - separate generated classes from props classes
      const generatedClasses = [
        `dezzy-${manifest.name.toLowerCase()}`,
        `dezzy-${manifest.name.toLowerCase()}--${variant}`
      ];
      
      const allClasses = [
        ...generatedClasses,
        manifest.defaultProps?.className,
        variantDef?.props?.className,
        otherProps.className
      ].filter(Boolean).join(' ');

      // Exclude className from mergedProps to avoid overriding our carefully constructed classes
      const { className: _, ...otherMergedProps } = mergedProps;
      
      return React.createElement(
        'div',
        {
          className: allClasses,
          style: { ...tokenStyles, ...mergedProps.style },
          'data-dezzy-section': manifest.name,
          'data-dezzy-variant': variant,
          'data-dezzy-category': manifest.category,
          role: manifest.accessibility?.role,
          'aria-label': manifest.accessibility?.ariaLabel,
          tabIndex: manifest.accessibility?.keyboardSupport ? 0 : undefined,
          ...otherMergedProps
        },
        this.renderSectionContent(manifest, mergedProps)
      );
    };
  }

  private renderSectionContent(manifest: SectionManifest, props: any): React.ReactNode {
    // This is where you would render the actual section content
    // For now, render a placeholder that shows the section structure
    
    const { children, title, subtitle, ...otherProps } = props;

    return React.createElement(
      React.Fragment,
      {},
      // Title if provided
      title && React.createElement('h3', { 
        className: 'dezzy-section-title',
        style: { color: 'var(--titleColor, inherit)' }
      }, title),
      
      // Subtitle if provided  
      subtitle && React.createElement('p', {
        className: 'dezzy-section-subtitle',
        style: { color: 'var(--subtitleColor, inherit)' }
      }, subtitle),
      
      // Main content
      React.createElement('div', {
        className: 'dezzy-section-content'
      }, children || `${manifest.name} Component`)
    );
  }

  // Generate TypeScript component from manifest
  generateTSXComponent(manifest: SectionManifest): string {
    const componentName = manifest.name;
    const interfaceName = `${componentName}Props`;
    
    // Generate props interface
    const propsInterface = this.generatePropsInterface(manifest);
    
    // Generate variant types
    const variantType = manifest.variants?.length > 0
      ? manifest.variants.map(v => `'${v.name}'`).join(' | ')
      : "'default'";
    
    // Generate component
    const component = `
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

${propsInterface}

const ${componentName.toLowerCase()}Variants = cva(
  'dezzy-${componentName.toLowerCase()}', // base classes
  {
    variants: {
      variant: {
${manifest.variants?.map(v => `        ${v.name}: '${v.props?.className || ''}',`).join('\n') || '        default: "",'}
      },
      size: {
        small: 'text-sm',
        medium: 'text-base', 
        large: 'text-lg'
      }
    },
    defaultVariants: {
      variant: '${manifest.variants?.[0]?.name || 'default'}',
      size: 'medium'
    }
  }
);

export const ${componentName}: React.FC<${interfaceName}> = ({
  variant = '${manifest.variants?.[0]?.name || 'default'}',
  className,
  children,
  ${Object.keys(manifest.props || {}).join(',\n  ')},
  ...props
}) => {
  return (
    <div
      className={${componentName.toLowerCase()}Variants({ variant, className })}
      data-dezzy-section="${manifest.name}"
      data-dezzy-variant={variant}
      ${manifest.accessibility?.role ? `role="${manifest.accessibility.role}"` : ''}
      {...props}
    >
      {children}
    </div>
  );
};

export type { ${interfaceName} };
`;

    return component;
  }

  private generatePropsInterface(manifest: SectionManifest): string {
    const componentName = manifest.name;
    const interfaceName = `${componentName}Props`;
    
    const props = manifest.props || {};
    const variantType = manifest.variants?.length > 0
      ? manifest.variants.map(v => `'${v.name}'`).join(' | ')
      : "'default'";
    
    const propLines = Object.entries(props).map(([name, config]) => {
      const optional = !config.required ? '?' : '';
      const type = this.mapPropTypeToTS(config.type);
      const comment = config.description ? `  /** ${config.description} */\n` : '';
      
      return `${comment}  ${name}${optional}: ${type};`;
    });

    return `
interface ${interfaceName} {
  /** Component variant */
  variant?: ${variantType};
  /** Additional CSS classes */
  className?: string;
  /** Component content */
  children?: React.ReactNode;
${propLines.join('\n')}
}`;
  }

  private mapPropTypeToTS(propType: string): string {
    const typeMap: Record<string, string> = {
      'string': 'string',
      'number': 'number',
      'boolean': 'boolean',
      'function': '() => void',
      'ReactNode': 'React.ReactNode',
      'object': 'Record<string, any>'
    };
    
    return typeMap[propType] || 'any';
  }

  // Generate DSL from component usage
  generateDSLFromUsage(componentName: string, props: Record<string, any>): ViewDSL {
    return {
      meta: {
        name: `${componentName} Usage`,
        id: `${componentName.toLowerCase()}-usage`,
        version: '1.0.0'
      },
      layout: {
        type: 'stack',
        gap: '$space.4'
      },
      sections: [
        {
          use: componentName,
          variant: props.variant || 'default',
          bindings: Object.fromEntries(
            Object.entries(props).filter(([key]) => key !== 'variant')
          )
        }
      ]
    };
  }

  // Agent workflow: Compile a full design system
  compileDesignSystem(manifests: SectionManifest[]): {
    components: Record<string, React.ComponentType<any>>;
    tsxFiles: Record<string, string>;
    registry: SectionRegistry;
  } {
    const components: Record<string, React.ComponentType<any>> = {};
    const tsxFiles: Record<string, string> = {};
    const registry: SectionRegistry = {};

    manifests.forEach(manifest => {
      // Compile to React component
      const component = this.compileSection(manifest);
      components[manifest.name] = component;
      registry[manifest.name] = component;
      
      // Generate TSX file
      tsxFiles[`${manifest.name}.tsx`] = this.generateTSXComponent(manifest);
    });

    return {
      components,
      tsxFiles, 
      registry
    };
  }
}